// SubCategory Closure Request Admin API Service Functions
import { fetchGetApi, fetchPostApi, fetchPutApi } from '@/util/api';

// Type definitions
interface ApiResponse {
  response: Response;
  data: any;
}

interface PendingApprovalFilters {
  priority?: string;
  isUrgent?: boolean;
}

/**
 * Get all pending closure requests that require admin approval
 * @param {PendingApprovalFilters} filters - Optional filters for priority and urgency
 * @returns {Promise<ApiResponse>} API response with pending requests
 */
export const getPendingApprovals = async (filters: PendingApprovalFilters = {}): Promise<ApiResponse> => {
  const queryParams = new URLSearchParams();
  
  // Add status filter for pending requests
  queryParams.append('status', 'pending');
  
  if (filters.priority) {
    queryParams.append('priority', filters.priority);
  }
  
  if (filters.isUrgent !== undefined) {
    queryParams.append('isUrgent', filters.isUrgent.toString());
  }
  
  const endpoint = `api/subcategory-closure/requests${queryParams.toString() ? `?${queryParams.toString()}` : '?status=pending'}`;
  return await fetchGetApi(endpoint);
};

/**
 * Get all closure requests (all statuses) for history view
 * @param {PendingApprovalFilters} filters - Optional filters for priority and urgency
 * @returns {Promise<ApiResponse>} API response with all requests
 */
export const getAllClosureRequests = async (filters: PendingApprovalFilters = {}): Promise<ApiResponse> => {
  const queryParams = new URLSearchParams();
  
  if (filters.priority) {
    queryParams.append('priority', filters.priority);
  }
  
  if (filters.isUrgent !== undefined) {
    queryParams.append('isUrgent', filters.isUrgent.toString());
  }
  
  const endpoint = `api/subcategory-closure/requests${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  return await fetchGetApi(endpoint);
};

/**
 * Approve a subcategory closure request
 * @param {string} requestId - The ID of the request to approve
 * @param {string} approvalComments - Optional approval comments
 * @returns {Promise<ApiResponse>} API response
 */
export const approveClosureRequest = async (requestId: string, approvalComments: string = ''): Promise<ApiResponse> => {
  const endpoint = `api/subcategory-closure/approve/${requestId}`;
  const body = { approvalComments };
  return await fetchPutApi(endpoint, body);
};

/**
 * Reject a subcategory closure request
 * @param {string} requestId - The ID of the request to reject
 * @param {string} rejectionReason - Required rejection reason
 * @returns {Promise<ApiResponse>} API response
 */
export const rejectClosureRequest = async (requestId: string, rejectionReason: string): Promise<ApiResponse> => {
  const endpoint = `api/subcategory-closure/reject/${requestId}`;
  const body = { rejectionReason };
  return await fetchPutApi(endpoint, body);
};

/**
 * Bulk approve multiple closure requests
 * @param {Array<string>} requestIds - Array of request IDs to approve
 * @param {string} approvalComments - Optional approval comments for all requests
 * @returns {Promise<ApiResponse>} API response with results breakdown
 */
export const bulkApproveRequests = async (requestIds: string[], approvalComments: string = ''): Promise<ApiResponse> => {
  const endpoint = 'api/subcategory-closure/bulk-approve';
  const body = { requestIds, approvalComments };
  return await fetchPostApi(endpoint, body);
};

/**
 * Get details of a specific closure request
 * @param {string} requestId - The ID of the request to get details for
 * @returns {Promise<ApiResponse>} API response with request details
 */
export const getClosureRequestDetails = async (requestId: string): Promise<ApiResponse> => {
  const endpoint = `api/subcategory-closure/details/${requestId}`;
  return await fetchGetApi(endpoint);
};

// Type definitions
interface ApiResponse {
  response: Response;
  data: any;
}

interface PendingApprovalFilters {
  priority?: string;
  isUrgent?: boolean;
}

interface StatusBadge {
  class: string;
  label: string;
}

interface PriorityBadge {
  class: string;
  label: string;
}

// Priority levels for display
export const PRIORITY_LEVELS = {
  HIGH: 'high',
  MEDIUM: 'medium',
  LOW: 'low'
} as const;

// Status badges configuration
export const STATUS_CONFIG: Record<string, StatusBadge> = {
  pending: {
    class: 'bg-yellow-100 text-yellow-800',
    label: 'Pending'
  },
  approved: {
    class: 'bg-green-100 text-green-800',
    label: 'Approved'
  },
  rejected: {
    class: 'bg-red-100 text-red-800',
    label: 'Rejected'
  }
};

// Priority badges configuration
export const PRIORITY_CONFIG: Record<string, PriorityBadge> = {
  high: {
    class: 'bg-red-100 text-red-800',
    label: 'High'
  },
  medium: {
    class: 'bg-yellow-100 text-yellow-800',
    label: 'Medium'
  },
  low: {
    class: 'bg-green-100 text-green-800',
    label: 'Low'
  }
};

/**
 * Format date for display
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted date
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Calculate if a request is overdue (created more than 2 days ago)
 * @param {string} createdAt - ISO date string
 * @returns {boolean} True if overdue
 */
export const isOverdue = (createdAt: string | null | undefined): boolean => {
  if (!createdAt) return false;
  const created = new Date(createdAt);
  const now = new Date();
  const diffDays = (now.getTime() - created.getTime()) / (1000 * 60 * 60 * 24);
  return diffDays > 2;
};